import classNames from 'classnames';
import { get, throttle } from 'lodash';
import PropTypes from 'prop-types';
import React from 'react';

import Button, { TYPES } from 'components/Button';
import { Container, Grid, Cell } from 'components/Grid';
import { DownloadIcon } from 'components/Icon';
import Image from 'components/ImageV2';
import { Small, MediumUp, LargeUp } from 'components/MediaQuery';
import RichText from 'components/RichText';
import { IMAGE_RATIOS, IMAGE_WIDTHS } from 'constants/images';
import { mediumUp, largeUp } from 'constants/mediaQueries';
import Theme from 'styles/theme';
import I18n from 'utils/I18n';

const BORDER_RADIUS = Theme.get('spacing') * 5;
const SCROLL_THROTTLE = 200;

class SustainabilityPageHero extends React.Component {
  static getTriggerOffset = $triggerSlide => {
    const positionFromTop = $triggerSlide.getBoundingClientRect().top;
    const triggerOffset = positionFromTop - window.innerHeight / 2;
    return triggerOffset;
  };

  updateSlideBackground = throttle(() => {
    const { setIsInverted } = this.props;
    const triggerSlide = this.triggerSlideRef.current;

    if (triggerSlide && window.innerHeight) {
      const triggerOffset = SustainabilityPageHero.getTriggerOffset(triggerSlide);
      if (triggerOffset < 0) {
        setIsInverted(true);
      } else {
        setIsInverted(false);
      }
    }
  }, SCROLL_THROTTLE);

  constructor() {
    super();
    this.triggerSlideRef = React.createRef();
  }

  componentDidMount() {
    window.addEventListener('scroll', this.updateSlideBackground);
    this.updateSlideBackground();
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.updateSlideBackground);
  }

  render() {
    const {
      heroCopy,
      heroCopyOverflow,
      heroImage,
      heroImageMobile,
      introSlideDark,
      introSlideLight,
      isInverted,
      zIndex,
      reportLink,
    } = this.props;
    const _heroImageMobile = heroImageMobile || heroImage;

    return (
      <div
        className={classNames('SustainabilityPageHero', {
          'SustainabilityPageHero--inverted': isInverted,
        })}
        style={{ zIndex: zIndex + 1 }}
      >
        <LargeUp>
          <Container>
            <Button
              type={TYPES.INVERSE}
              linkTo={reportLink}
              target="_blank"
              className="SustainabilityPageHero__report-button"
            >
              {I18n.localize('sustainabilityPage.report')}
              <DownloadIcon />
            </Button>
          </Container>
        </LargeUp>
        <div className="SustainabilityPageHero__image-wrapper">
          <Small>
            <Image
              className="SustainabilityPageHero__image"
              src={get(_heroImageMobile, ['file', 'url'])}
              alt={get(_heroImageMobile, ['description'])}
              width={IMAGE_WIDTHS.W_1000}
              ratio={IMAGE_RATIOS.R_5x6}
            />
          </Small>
          <MediumUp>
            <Image
              className="SustainabilityPageHero__image"
              src={get(heroImage, ['file', 'url'])}
              alt={get(heroImage, ['description'])}
              width={IMAGE_WIDTHS.W_2000}
              ratio={IMAGE_RATIOS.R_16x9}
            />
          </MediumUp>
        </div>
        <div className="SustainabilityPageHero__image-copy">
          <Container>
            <Grid isCentered>
              <Cell smallUp={{ columns: 12 }} mediumUp={{ columns: 11 }} largeUp={{ columns: 8 }}>
                <RichText content={heroCopy} />
              </Cell>
            </Grid>
          </Container>
        </div>
        <div className="SustainabilityPageHero__overflow-copy">
          <Container>
            <Grid isCentered>
              <Cell smallUp={{ columns: 12 }} mediumUp={{ columns: 11 }} largeUp={{ columns: 8 }}>
                <RichText content={heroCopyOverflow} />
              </Cell>
            </Grid>
          </Container>
        </div>
        <div
          ref={this.triggerSlideRef}
          className={classNames('SustainabilityPageHero__top-slide', {
            'SustainabilityPageHero__top-slide--inverted': isInverted,
          })}
        >
          <Container>
            <Grid isCentered>
              <Cell smallUp={{ columns: 12 }} mediumUp={{ columns: 11 }} largeUp={{ columns: 8 }}>
                <RichText content={introSlideDark} />
              </Cell>
            </Grid>
          </Container>
        </div>
        <div className="SustainabilityPageHero__bottom-slide">
          <Container>
            <Grid isCentered>
              <Cell smallUp={{ columns: 12 }} mediumUp={{ columns: 11 }} largeUp={{ columns: 8 }}>
                <RichText content={introSlideLight} />
              </Cell>
            </Grid>
          </Container>
        </div>
        <style jsx>
          {`
            .SustainabilityPageHero {
              position: relative;
              z-index: 999;
              border-radius: 0 0 ${BORDER_RADIUS}px ${BORDER_RADIUS}px;
            }

            .SustainabilityPageHero__image-wrapper {
              position: absolute;
              width: 100vw;
              min-height: ${Theme.get('page-height-px')};
            }

            .SustainabilityPageHero__image-copy {
              position: relative;
              padding: 92px 0px 50px 0px;
            }

            @media ${largeUp} {
              .SustainabilityPageHero__image-copy {
                padding: 180px 0px 110px 0px;
              }
            }

            .SustainabilityPageHero__overflow-copy {
              padding: 40px 0px 70px 0px;
            }

            @media ${largeUp} {
              .SustainabilityPageHero__overflow-copy {
                padding: 40px 0px 120px;
              }
            }

            .SustainabilityPageHero__top-slide {
              color: ${Theme.get('colors-white')};
              padding-top: 200px;
            }

            .SustainabilityPageHero__bottom-slide {
              color: ${Theme.get('colors-black')};
              padding-bottom: ${Theme.get('spacing') * 20}px;
            }

            @media ${largeUp} {
              .SustainabilityPageHero__bottom-slide {
                padding-bottom: 120px;
                padding-top: 80px;
              }
            }

            .SustainabilityPageHero {
              background-color: ${Theme.get('colors-black')};
              transition: background-color ${Theme.get('transition-duration-ms')} ease-in-out;
            }

            .SustainabilityPageHero--inverted {
              background-color: ${Theme.get('colors-white')};
            }
          `}
        </style>
        <style jsx global>
          {`
            .SustainabilityPageHero__report-button.Button {
              z-index: 1;
              float: right;
              margin-top: ${Theme.get('spacing') * 9}px;
              border-radius: 22px;
              display: flex;
              justify-content: center;
              border-color: transparent;
            }

            .SustainabilityPageHero__report-button .Icon--DOWNLOAD {
              margin-left: ${Theme.get('spacing') * 2}px;
            }

            .SustainabilityPageHero__report-button:hover .Icon--DOWNLOAD img {
              filter: invert(1);
            }

            .SustainabilityPageHero .Paragraph,
            .SustainabilityPageHero .ListItem {
              font-weight: bold;
              font-size: 18px;
            }

            @media ${mediumUp} {
              .SustainabilityPageHero .Paragraph,
              .SustainabilityPageHero .ListItem {
                font-size: 22px;
              }
            }

            .SustainabilityPageHero__image {
              display: block;
              width: 100%;
              height: 100%;
              object-fit: cover;
            }

            @media ${mediumUp} {
              .SustainabilityPageHero__image {
                display: block;
                width: 100%;
                height: 120%;
                object-fit: cover;
              }
            }

            .SustainabilityPageHero__image-copy .typography--primary-heading,
            .SustainabilityPageHero__image-copy .Paragraph,
            .SustainabilityPageHero__image-copy .ListItem {
              color: ${Theme.get('colors-white')};
            }

            .SustainabilityPageHero__overflow-copy .typography--primary-heading,
            .SustainabilityPageHero__overflow-copy .Paragraph,
            .SustainabilityPageHero__overflow-copy .ListItem {
              color: ${Theme.get('colors-white')};
            }

            .SustainabilityPageHero__top-slide .typography--primary-heading,
            .SustainabilityPageHero__top-slide .Paragraph,
            .SustainabilityPageHero__top-slide .ListItem {
              color: ${Theme.get('colors-white')};
            }

            .SustainabilityPageHero__top-slide--inverted .typography--primary-heading {
              color: ${Theme.get('colors-white')};
            }

            .SustainabilityPageHero .RichText > .typography--primary-heading:last-child,
            .SustainabilityPageHero .RichText > .Paragraph:last-child,
            .SustainabilityPageHero .RichText > .ListItem:last-child {
              margin-bottom: 0;
            }
          `}
        </style>
      </div>
    );
  }
}

SustainabilityPageHero.propTypes = {
  heroCopy: PropTypes.shape({}),
  heroCopyOverflow: PropTypes.shape({}),
  heroImage: PropTypes.shape({}),
  heroImageMobile: PropTypes.shape({}),
  introSlideDark: PropTypes.shape({}),
  introSlideLight: PropTypes.shape({}),
  isInverted: PropTypes.bool,
  setIsInverted: PropTypes.func.isRequired,
  zIndex: PropTypes.number.isRequired,
  reportLink: PropTypes.string.isRequired,
};

SustainabilityPageHero.defaultProps = {
  heroCopy: {},
  heroCopyOverflow: {},
  heroImage: {},
  heroImageMobile: null,
  introSlideDark: {},
  introSlideLight: {},
  isInverted: false,
};

export default SustainabilityPageHero;
